import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import PropertyService from "@/services/property";
import parseHttpError from "@/utils/parseHttpError";
import {
  PlatformLocationCity,
  PlatformLocationState,
  PlatformLocationTotals,
  PlatformLocationsData,
} from "@/types/property";

export const PLATFORM_LOCATIONS_CACHE_TTL_MS = 10 * 60 * 1000;

const CACHE_PREFIX = "fnp:platform-locations:v1";

interface CachedPlatformLocations {
  cachedAt: number;
  data: PlatformLocationsData;
}

export interface UsePlatformLocationsOptions {
  state?: string;
  enabled?: boolean;
  cacheTtlMs?: number;
}

export interface UsePlatformLocationsResult {
  data: PlatformLocationsData | null;
  states: PlatformLocationState[];
  cities: PlatformLocationCity[];
  totals: PlatformLocationTotals | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  refresh: () => Promise<void>;
}

function normalizeState(state?: string): string | undefined {
  const normalized = state?.trim().toLowerCase();
  return normalized || undefined;
}

function cacheKey(state?: string): string {
  return `${CACHE_PREFIX}:${state ?? "all"}`;
}

function readCache(key: string): CachedPlatformLocations | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachedPlatformLocations;
    if (
      !parsed ||
      typeof parsed.cachedAt !== "number" ||
      !parsed.data ||
      !Array.isArray(parsed.data.states)
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: PlatformLocationsData): void {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({ cachedAt: Date.now(), data }),
    );
  } catch {
    // Storage can be unavailable (private mode/quota); the query still works.
  }
}

export function clearPlatformLocationsCache(state?: string): void {
  if (typeof window === "undefined") return;

  const normalized = normalizeState(state);
  if (normalized) {
    window.sessionStorage.removeItem(cacheKey(normalized));
    return;
  }

  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith(CACHE_PREFIX)) {
      window.sessionStorage.removeItem(key);
    }
  }
}

export function usePlatformLocations(
  options: UsePlatformLocationsOptions = {},
): UsePlatformLocationsResult {
  const normalizedState = normalizeState(options.state);
  const cacheTtlMs = options.cacheTtlMs ?? PLATFORM_LOCATIONS_CACHE_TTL_MS;
  const key = cacheKey(normalizedState);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: queryRefetch,
  } = useQuery<PlatformLocationsData, Error>({
    queryKey: ["platform-locations", normalizedState ?? "all"],
    queryFn: async () => {
      const cached = readCache(key);
      if (cached && Date.now() - cached.cachedAt < cacheTtlMs) {
        return cached.data;
      }

      try {
        const response = await PropertyService.GetPlatformLocations(
          normalizedState ? { state: normalizedState } : undefined,
        );
        const data = response.data?.data;

        if (!data || !Array.isArray(data.states)) {
          throw new Error("Invalid locations response");
        }

        writeCache(key, data);
        return data;
      } catch (error) {
        if (cached) return cached.data;
        throw new Error(parseHttpError(error).message);
      }
    },
    enabled: options.enabled ?? true,
    staleTime: cacheTtlMs,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const refresh = useCallback(async () => {
    clearPlatformLocationsCache(normalizedState);
    await queryRefetch();
  }, [normalizedState, queryRefetch]);

  const refetch = useCallback(() => {
    void queryRefetch();
  }, [queryRefetch]);

  return {
    data: data ?? null,
    states: data?.states ?? [],
    cities: normalizedState
      ? data?.states.find((state) => state.slug === normalizedState)?.cities ??
        []
      : [],
    totals: data?.totals ?? null,
    isLoading,
    isFetching,
    isError,
    error: error ?? null,
    refetch,
    refresh,
  };
}

export default usePlatformLocations;
